import { SyntheticEvent, useContext } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { debounce, filter } from 'lodash';
import { observer } from 'mobx-react-lite';
import {
  ActivitiesUsersLookupItemType,
  ActivityUsersLookup,
} from 'src/api/api-types/assets';
import { lookupsStoreCtx } from 'src/stores/lookups';
import { isPending } from 'src/utils/common';
import { DEBOUNCE_DURATION } from 'src/utils/constants';

import { useAssetsStore } from '../../AssetsProvider';

interface ActivityUserEditingType {
  id: number | string;
  value: ActivitiesUsersLookupItemType | null;
  field: string;
}

const ActivityUserEditing = observer(
  ({ id, field, value }: ActivityUserEditingType) => {
    const apiRef = useGridApiContext();
    const { chosenAsset } = useAssetsStore();

    if (!chosenAsset)
      return (
        <Typography sx={{ pl: 1 }}>
          {value?.name || 'No user selected'}
        </Typography>
      );
    const { relatedToType, relatedTo } = chosenAsset;

    const {
      searchActivitiesUsersReq,
      searchedActivitiesUsers,
      searchActivitiesUsers,
    } = useContext(lookupsStoreCtx);

    const onInputChange = debounce(
      async (_: SyntheticEvent, value: string, reason: string) => {
        if (reason.includes('input') && relatedToType) {
          await searchActivitiesUsers({
            'relatedToType.id':
              relatedToType?.id as ActivityUsersLookup['relatedToType.id'],
            'relatedTo.id': relatedTo?.id,
            ...(value && { name: value }),
          });
        }
      },
      DEBOUNCE_DURATION,
      {
        leading: false,
        trailing: true,
      },
    );

    const handleChange = (data: ActivitiesUsersLookupItemType | null) => {
      (async () => {
        return apiRef.current.setEditCellValue({
          id,
          field,
          value: data,
        });
      })();
    };

    const onInputFocus = async () => {
      if (relatedToType && relatedTo) {
        await searchActivitiesUsers({
          'relatedToType.id':
            relatedToType?.id as ActivityUsersLookup['relatedToType.id'],
          'relatedTo.id': relatedTo.id,
          accessMode: 1 as const,
        });
      }
    };

    const filterOptions = (
      options: ActivitiesUsersLookupItemType[],
      params: FilterOptionsState<ActivitiesUsersLookupItemType>,
    ): ActivitiesUsersLookupItemType[] => {
      const defaultFiltered = filter(options, params);
      if (defaultFiltered.length === 0) {
        return options;
      }
      return defaultFiltered as ActivitiesUsersLookupItemType[];
    };

    return (
      <Autocomplete
        onFocus={onInputFocus}
        value={value}
        defaultValue={value}
        onChange={(_, data) => handleChange(data)}
        disablePortal={false}
        fullWidth
        disabled={!relatedToType}
        filterOptions={filterOptions}
        getOptionLabel={option => option?.name || ''}
        isOptionEqualToValue={(option, val) => option?.id === val?.id}
        options={searchedActivitiesUsers || []}
        onInputChange={onInputChange}
        loading={isPending(searchActivitiesUsersReq)}
        componentsProps={{
          popper: {
            modifiers: [
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  boundary: 'window',
                },
              },
            ],
          },
        }}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.id}
            style={{
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}>
            <Typography color="primary">{option.name}</Typography>
            <Typography variant="caption">{option.email}</Typography>
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            placeholder="User name"
            sx={{ p: 0, border: 0 }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isPending(searchActivitiesUsersReq) && (
                    <CircularProgress color="inherit" size={20} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  },
);

export default ActivityUserEditing;
